import React, { useState, useEffect } from 'react';
import { Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../components/auth';
import { SignUpForm, SignInForm, ConfirmSignUpForm, ForgotPasswordForm } from '../components/auth';
import { CallbackHandler } from '../components/auth/CallbackHandler';
import { SuccessToast } from '../components/ui/SuccessToast';
import '../components/auth/CallbackHandler.css';

export const AuthPage: React.FC = () => {
  const { isAuthenticated } = useAuth();
  const [pendingEmail, setPendingEmail] = useState<string>('');
  const [successMessage, setSuccessMessage] = useState<string>('');
  const location = useLocation();
  const navigate = useNavigate();

  // Check for success messages in URL parameters
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const success = urlParams.get('success');
    
    if (success === 'signup') {
      setSuccessMessage(
        'Account created successfully! Please check your email for a verification link before signing in.'
      );
      // Clean up URL parameters
      navigate(location.pathname, { replace: true });
    } else if (success === 'confirmed') {
      setSuccessMessage('Email verified successfully! You can now sign in to your account.');
      navigate(location.pathname, { replace: true });
    } else if (success === 'password-reset') {
      setSuccessMessage('Password reset successfully! You can now sign in with your new password.');
      navigate(location.pathname, { replace: true });
    }
  }, [location.search, location.pathname, navigate]);

  // Redirect if already authenticated
  if (isAuthenticated) {
    return <Navigate to="/chat" replace />;
  }

  // Removed unused function

  const handleConfirmationSuccess = () => {
    // Redirect to sign in after successful confirmation with success message
    window.location.href = '/auth/signin?success=confirmed';
  };

  const handlePasswordResetSuccess = () => {
    // Redirect to sign in after successful password reset with success message
    window.location.href = '/auth/signin?success=password-reset';
  };

  return (
    <>
      {/* Success Toast */}
      {successMessage && (
        <SuccessToast
          message={successMessage}
          onDismiss={() => setSuccessMessage('')}
          position="top-center"
        />
      )}

      <Routes>
      <Route 
        path="signup" 
        element={
          <SignUpForm 
            onSuccess={(email) => {
              setPendingEmail(email);
              // Redirect to sign in with success message instead of confirmation page
              window.location.href = '/auth/signin?success=signup';
            }}
            onSwitchToLogin={() => window.location.href = '/auth/signin'}
          />
        } 
      />
      
      <Route 
        path="signin" 
        element={
          <SignInForm 
            onSuccess={() => window.location.href = '/chat'}
            onSwitchToSignUp={() => window.location.href = '/auth/signup'}
            onForgotPassword={() => window.location.href = '/auth/forgot-password'}
          />
        } 
      />
      
      <Route 
        path="confirm" 
        element={
          pendingEmail ? (
            <ConfirmSignUpForm 
              email={pendingEmail}
              onSuccess={handleConfirmationSuccess}
              onBackToSignUp={() => window.location.href = '/auth/signup'}
            />
          ) : (
            <Navigate to="/auth/signup" replace />
          )
        } 
      />
      
      <Route 
        path="forgot-password" 
        element={
          <ForgotPasswordForm 
            onSuccess={handlePasswordResetSuccess}
            onBackToSignIn={() => window.location.href = '/auth/signin'}
          />
        } 
      />
      
      {/* Email verification callback handler */}
      <Route 
        path="callback" 
        element={<CallbackHandler />} 
      />
      
      {/* Default redirect to sign in */}
      <Route path="*" element={<Navigate to="/auth/signin" replace />} />
    </Routes>
    </>
  );
};